/**
 * /ai — how to use CherryClipz from Claude or ChatGPT. Public and login-free: it is the
 * documentation link both AI app directories ask for, so their reviewers open it signed out,
 * and it is the page we send people to ("connect it like this"): the film, the address to copy,
 * three steps per assistant, then the details. Same look as /privacy (LegalPage); the words live
 * in connectorGuideConstants.ts.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { brandPageCss } from "../brand/brandPageCss";
import { BrandNav, BrandFooter, useBrandTheme } from "../brand/BrandChrome";
import { LEGAL_PAGE_CSS } from "../legal/LegalPage";
import {
  CONNECTOR_GUIDE_UPDATED,
  CONNECTOR_GUIDE_TITLE,
  CONNECTOR_GUIDE_INTRO,
  CONNECTOR_GUIDE_SECTIONS,
  CONNECTOR_MCP_URL,
  CONNECTOR_VIDEO,
  CONNECTOR_CONNECT_HEADING,
  CONNECTOR_CONNECT_NOTE,
  CONNECTOR_ADDRESS_LABEL,
  CONNECTOR_COPY_LABEL,
  CONNECTOR_COPIED_LABEL,
  CONNECTOR_IN_CHAT_LABEL,
  CONNECTOR_APPS,
  CONNECTOR_ASK_HEADING,
  CONNECTOR_ASK_STEPS,
  CONNECTOR_EXAMPLES_LABEL,
  CONNECTOR_EXAMPLES,
  CONNECTOR_DETAILS_HEADING,
} from "./connectorGuideConstants";

const SCOPE = "cclg";

/** Copy the server address; falls back to selecting it when the clipboard API is blocked. */
const AddressBox: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const textRef = useRef<HTMLElement>(null);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const selectText = () => {
    try {
      const range = document.createRange();
      if (textRef.current) range.selectNodeContents(textRef.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch (e) {
      /* ignore */
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_MCP_URL);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      selectText();
    }
  };

  return (
    <div className="addr">
      <span className="addr-label eyebrow">{CONNECTOR_ADDRESS_LABEL}</span>
      <div className="addr-row">
        <code className="addr-url" ref={textRef} onClick={selectText}>
          {CONNECTOR_MCP_URL}
        </code>
        <button className="btn btn-cherry addr-copy" onClick={copy} aria-live="polite">
          {copied ? CONNECTOR_COPIED_LABEL : CONNECTOR_COPY_LABEL}
        </button>
      </div>
    </div>
  );
};

const ConnectorGuidePage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useBrandTheme();

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + LEGAL_PAGE_CSS + CONNECT_CSS}</style>
      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PRIVACY)}>
          Privacy
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.CONTACT)}>
          Help
        </button>
      </BrandNav>

      <div className="wrap hero">
        <span className="eyebrow">Updated {CONNECTOR_GUIDE_UPDATED}</span>
        <h1>{CONNECTOR_GUIDE_TITLE}</h1>
        <p className="sub">{CONNECTOR_GUIDE_INTRO}</p>
      </div>

      <div className="wrap film">
        <video
          className="film-video"
          src={CONNECTOR_VIDEO.src}
          poster={CONNECTOR_VIDEO.poster}
          controls
          playsInline
          preload="none"
        />
        <p className="film-cap">{CONNECTOR_VIDEO.caption}</p>
      </div>

      <section className="wrap connect" id="connect">
        <div className="sec-head">
          <h2>{CONNECTOR_CONNECT_HEADING}</h2>
          <p>{CONNECTOR_CONNECT_NOTE}</p>
        </div>
        <AddressBox />
        <div className="apps">
          {CONNECTOR_APPS.map((app) => (
            <div className="card app" key={app.id}>
              <h3>{app.name}</h3>
              <ol className="nums">
                {app.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p className="in-chat">
                <strong>{CONNECTOR_IN_CHAT_LABEL}:</strong> {app.inChat}
              </p>
              <a className="btn btn-ghost app-open" href={app.openUrl} target="_blank" rel="noopener noreferrer">
                {app.openLabel} ↗
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="wrap ask">
        <div className="sec-head">
          <h2>{CONNECTOR_ASK_HEADING}</h2>
        </div>
        <ol className="nums">
          {CONNECTOR_ASK_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <h3 className="ex-h">{CONNECTOR_EXAMPLES_LABEL}</h3>
        <ul className="ex">
          {CONNECTOR_EXAMPLES.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      </section>

      <section className="wrap doc">
        <h2 className="doc-h">{CONNECTOR_DETAILS_HEADING}</h2>
        {CONNECTOR_GUIDE_SECTIONS.map((s) => (
          <div className="blk" key={s.heading}>
            <h2>{s.heading}</h2>
            {s.paragraphs?.map((p) => (
              <p key={p}>{p}</p>
            ))}
            {s.bullets && (
              <ul>
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        <div className="blk">
          <h2>Privacy and support</h2>
          <p>
            What the assistant can see is in our{" "}
            <a href={ROUTES.PRIVACY}>privacy policy</a>, under &ldquo;Using CherryClipz from Claude
            or ChatGPT&rdquo;. Questions: <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
          </p>
        </div>
      </section>

      <BrandFooter>
        <button className="linkbtn" onClick={() => navigate(ROUTES.PRIVACY)}>
          Privacy policy
        </button>
      </BrandFooter>
    </div>
  );
};

const CONNECT_CSS = `
.cclg .film{display:flex;flex-direction:column;align-items:center;gap:12px;padding-top:36px}
.cclg .film-video{display:block;width:min(300px,100%);aspect-ratio:9/16;border-radius:18px;background:#000;box-shadow:var(--shadow);border:1px solid var(--line)}
.cclg .film-cap{margin:0;color:var(--faint);font-size:13.5px;text-align:center;max-width:44ch}
.cclg .hero .sub{margin:0 auto}
.cclg .connect,.cclg .ask{margin-top:48px;padding-top:44px;padding-bottom:0}
.cclg .addr{display:flex;flex-direction:column;gap:8px;margin-bottom:22px}
.cclg .addr-row{display:flex;align-items:center;gap:10px;background:var(--surface);border:1px solid var(--line-2);border-radius:14px;padding:8px 8px 8px 16px}
.cclg .addr-url{flex:1;min-width:0;font-family:var(--mono);font-size:14px;color:var(--ink);overflow-wrap:anywhere;cursor:text}
.cclg .addr-copy{flex:none;min-width:96px;padding:10px 18px}
.cclg .apps{display:grid;grid-template-columns:1fr 1fr;gap:18px}
.cclg .app{display:flex;flex-direction:column;gap:14px}
.cclg .app h3{font-size:22px}
.cclg .nums{margin:0;padding:0;list-style:none;counter-reset:n;display:flex;flex-direction:column;gap:12px}
.cclg .nums li{counter-increment:n;position:relative;padding-left:38px;color:var(--muted);font-size:15px;line-height:1.55}
.cclg .nums li::before{content:counter(n);position:absolute;left:0;top:-1px;width:26px;height:26px;border-radius:50%;background:var(--cherry);color:#fff;font-weight:700;font-size:13px;display:grid;place-items:center}
.cclg .in-chat{margin:2px 0 0;padding-top:14px;border-top:1px solid var(--line);color:var(--muted);font-size:14.5px}
.cclg .in-chat strong{color:var(--ink)}
.cclg .app-open{margin-top:auto;align-self:flex-start;padding:10px 18px;font-size:14px}
.cclg .app-open:hover{text-decoration:none}
.cclg .ask .nums{max-width:64ch}
.cclg .ex-h{font-size:17px;margin:30px 0 12px}
.cclg .ex{margin:0;padding:0;list-style:none;display:flex;flex-wrap:wrap;gap:10px}
.cclg .ex li{background:var(--surface);border:1px solid var(--line);border-radius:12px;padding:10px 14px;color:var(--ink);font-size:14.5px;line-height:1.45}
.cclg .doc{margin-top:48px}
.cclg .doc-h{font-size:clamp(22px,2.6vw,30px)}
@media (max-width:720px){ .cclg .apps{grid-template-columns:1fr} }
@media (max-width:640px){ .cclg .wrap{padding-left:16px;padding-right:16px} .cclg .addr-row{flex-wrap:wrap} .cclg .addr-copy{width:100%} }
`;

export default ConnectorGuidePage;
